import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import './ConditionBuilder.scss';

// ── helpers ──

const defaultCondition = (defaults = {}) => ({
  field: '',
  operator: 'CONTAINS',
  value: '',
  caseSensitive: false,
  ...defaults
});

const defaultGroup = (defaults = {}) => ({
  operator: 'OR',       // intra‑group operator (conditions inside the group)
  conditions: [defaultCondition()],
  ...defaults
});

/**
 * Convert a flat conditions array (from the backend / existing rules) into
 * an array of condition groups.
 *
 * Grouping logic:
 *   – The second condition's operator sets the first group's intra‑operator.
 *   – Subsequent conditions whose operator matches the current group's
 *     intra‑operator are added to the same group.
 *   – When the operator changes, a new group boundary is created.
 *
 * A single‑condition rule results in one group with one condition.
 */
export const flatToGroups = (conditions) => {
  if (!conditions || conditions.length === 0) {
    return [defaultGroup()];
  }

  if (conditions.length === 1) {
    return [{ operator: 'OR', conditions: [{ ...conditions[0] }] }];
  }

  // The second condition's operator defines the first group's intra‑operator
  const firstGroupOp = conditions[1].logicalOperator || 'AND';
  const groups = [];
  let currentGroup = {
    operator: firstGroupOp,
    conditions: [{ ...conditions[0] }]
  };

  for (let i = 1; i < conditions.length; i++) {
    const cond = conditions[i];
    const curOp = cond.logicalOperator || 'AND';

    if (curOp === currentGroup.operator) {
      // Same operator as the group → stays in this group
      currentGroup.conditions.push({ ...cond });
    } else {
      // Operator changed → group boundary
      // The connector between old group and new group is curOp
      currentGroup.connector = curOp;
      groups.push(currentGroup);
      // Peek at next condition to determine the new group's intra‑operator
      const nextOp = conditions[i + 1]?.logicalOperator;
      currentGroup = {
        operator: nextOp || curOp,
        conditions: [{ ...cond }]
      };
    }
  }
  groups.push(currentGroup);
  return groups;
};

/**
 * Flatten condition groups back into the flat conditions array for the backend.
 *
 * Within a group every condition gets the group's intra‑operator.
 * The first condition of a *subsequent* group gets the inter‑group connector
 * from the previous group.
 */
export const groupsToFlat = (groups) => {
  const flat = [];
  groups.forEach((group, gi) => {
    group.conditions.forEach((cond, ci) => {
      const entry = { ...cond };
      if (gi === 0 && ci === 0) {
        // Very first condition — no logicalOperator
        delete entry.logicalOperator;
      } else if (ci === 0) {
        // First condition of a later group — use the inter‑group connector
        entry.logicalOperator = groups[gi - 1].connector || 'AND';
      } else {
        // Subsequent condition inside the same group — intra‑group operator
        entry.logicalOperator = group.operator;
      }
      flat.push(entry);
    });
  });
  return flat;
};

// ── component ──

const ConditionBuilder = ({
  conditions,
  onConditionsChange,
  errors = {},
  excelHeaders = [],
  isEditMode = false,
  defaultLogicalOperator = 'AND'
}) => {

  // ── dropdown options ──

  const defaultFieldOptions = [];

  const baseOptions = excelHeaders && excelHeaders.length > 0
    ? excelHeaders.map(h => ({ label: h, value: h }))
    : defaultFieldOptions;

  const existingFieldValues = isEditMode
    ? (conditions || [])
        .map(c => c.field)
        .filter(f => f && !baseOptions.find(o => o.value === f))
        .map(f => ({ label: f, value: f }))
    : [];

  const fieldOptions = [...baseOptions, ...existingFieldValues];

  const operatorOptions = [
    { label: 'Contains', value: 'CONTAINS' },
    { label: 'Equals', value: 'EQUALS' },
    { label: 'Starts With', value: 'STARTS_WITH' },
    { label: 'Ends With', value: 'ENDS_WITH' },
    { label: 'Regex', value: 'REGEX' },
    { label: 'Not Contains', value: 'NOT_CONTAINS' },
    { label: 'Not Equals', value: 'NOT_EQUALS' }
  ];

  const logicalOperatorOptions = [
    { label: 'AND', value: 'AND' },
    { label: 'OR', value: 'OR' }
  ];

  // ── manage groups as local state ──
  // We keep groups in local state rather than re-deriving from flat conditions
  // on every render, because the flat→groups round-trip is lossy (the first
  // condition has no logicalOperator, which can cause false group splits).

  const [groups, setGroups] = useState(() => flatToGroups(conditions));
  const lastEmittedRef = useRef(null);

  // Re-derive groups only when conditions change externally
  // (e.g. switching between create / edit mode)
  useEffect(() => {
    if (lastEmittedRef.current !== conditions) {
      setGroups(flatToGroups(conditions));
    }
  }, [conditions]);

  /** Push a groups change through the flat‑conditions callback */
  const emitGroups = (nextGroups) => {
    setGroups(nextGroups);
    const flat = groupsToFlat(nextGroups);
    lastEmittedRef.current = flat;
    onConditionsChange(flat);
  };

  // ── group‑level handlers ──

  const handleAddGroup = () => {
    const newGroups = [
      ...groups.map((g, i) => {
        // set connector on the last group if missing
        if (i === groups.length - 1 && !g.connector) {
          return { ...g, connector: defaultLogicalOperator };
        }
        return g;
      }),
      defaultGroup()
    ];
    emitGroups(newGroups);
  };

  const handleRemoveGroup = (groupIndex) => {
    const newGroups = groups.filter((_, i) => i !== groupIndex);
    // clean up connector on the new last group
    if (newGroups.length > 0) {
      const last = newGroups.length - 1;
      newGroups[last] = { ...newGroups[last] };
      delete newGroups[last].connector;
    }
    emitGroups(newGroups.length > 0 ? newGroups : [defaultGroup()]);
  };

  const handleGroupOperatorChange = (groupIndex, operator) => {
    const newGroups = [...groups];
    newGroups[groupIndex] = { ...newGroups[groupIndex], operator };
    emitGroups(newGroups);
  };

  const handleConnectorChange = (groupIndex, value) => {
    const newGroups = [...groups];
    newGroups[groupIndex] = { ...newGroups[groupIndex], connector: value };
    emitGroups(newGroups);
  };

  // ── condition‑level handlers (within a group) ──

  const handleAddCondition = (groupIndex) => {
    const newGroups = [...groups];
    const group = { ...newGroups[groupIndex] };
    // Carry over the field from the last condition in this group
    const lastCondition = group.conditions[group.conditions.length - 1];
    const defaults = lastCondition?.field ? { field: lastCondition.field } : {};
    group.conditions = [...group.conditions, defaultCondition(defaults)];
    newGroups[groupIndex] = group;
    emitGroups(newGroups);
  };

  const handleRemoveCondition = (groupIndex, condIndex) => {
    const newGroups = [...groups];
    const group = { ...newGroups[groupIndex] };
    group.conditions = group.conditions.filter((_, i) => i !== condIndex);
    if (group.conditions.length === 0) {
      // removing the last condition removes the group
      handleRemoveGroup(groupIndex);
      return;
    }
    newGroups[groupIndex] = group;
    emitGroups(newGroups);
  };

  const handleConditionChange = (groupIndex, condIndex, field, value) => {
    const newGroups = [...groups];
    const group = { ...newGroups[groupIndex] };
    group.conditions = [...group.conditions];
    group.conditions[condIndex] = { ...group.conditions[condIndex], [field]: value };
    newGroups[groupIndex] = group;
    emitGroups(newGroups);
  };

  // ── map group/condition indices back to flat index for error keys ──
  const flatIndex = (gi, ci) => {
    let idx = 0;
    for (let g = 0; g < gi; g++) {
      idx += groups[g].conditions.length;
    }
    return idx + ci;
  };

  // ── render ──

  return (
    <div className="condition-builder">
      <div className="condition-header">
        <h4>Condition Groups</h4>
        <div className="condition-header-actions">
          <Button
            label="Add Group"
            icon="pi pi-plus-circle"
            className="add-group-btn p-button-sm p-button-outlined"
            onClick={handleAddGroup}
            type="button"
          />
        </div>
      </div>

      {groups.length === 0 && (
        <div className="no-conditions">
          <p>No condition groups yet. Click "Add Group" to create one.</p>
        </div>
      )}

      {groups.map((group, gi) => (
        <React.Fragment key={gi}>
          {/* ── inter‑group connector ── */}
          {gi > 0 && (
            <div className="group-connector">
              <div className="group-connector-line" />
              <Dropdown
                value={groups[gi - 1].connector || 'AND'}
                options={logicalOperatorOptions}
                onChange={(e) => handleConnectorChange(gi - 1, e.value)}
                className="connector-dropdown"
              />
              <div className="group-connector-line" />
            </div>
          )}

          {/* ── group card ── */}
          <div className="condition-group-card">
            <div className="group-card-header">
              <div className="group-card-title">
                <span className="group-label">Group {gi + 1}</span>
                <span className="group-operator-label">Intra‑group:</span>
                <Dropdown
                  value={group.operator}
                  options={logicalOperatorOptions}
                  onChange={(e) => handleGroupOperatorChange(gi, e.value)}
                  className="group-operator-dropdown"
                />
              </div>
              <div className="group-card-actions">
                <Button
                  label="Add Condition"
                  icon="pi pi-plus"
                  className="p-button-sm p-button-text"
                  onClick={() => handleAddCondition(gi)}
                  type="button"
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-danger p-button-text p-button-sm"
                  onClick={() => handleRemoveGroup(gi)}
                  disabled={groups.length === 1 && group.conditions.length === 1}
                  tooltip="Remove group"
                  type="button"
                />
              </div>
            </div>

            {group.conditions.map((condition, ci) => {
              const fi = flatIndex(gi, ci);
              return (
                <div key={ci} className="condition-row">
                  {ci > 0 && (
                    <div className="condition-connector">
                      <span className="connector-label">{group.operator}</span>
                    </div>
                  )}
                  <div className="condition-fields">
                    <div className="field-group">
                      <label>Field</label>
                      <Dropdown
                        value={condition.field}
                        options={fieldOptions}
                        onChange={(e) => handleConditionChange(gi, ci, 'field', e.value)}
                        placeholder={fieldOptions.length === 0 ? "Upload Excel to load fields" : "Select Field"}
                        editable={isEditMode}
                        className={errors?.[`condition_${fi}_field`] ? 'p-invalid' : ''}
                      />
                      {errors?.[`condition_${fi}_field`] && (
                        <small className="p-error">{errors[`condition_${fi}_field`]}</small>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Operator</label>
                      <Dropdown
                        value={condition.operator}
                        options={operatorOptions}
                        onChange={(e) => handleConditionChange(gi, ci, 'operator', e.value)}
                        placeholder="Operator"
                        className={errors?.[`condition_${fi}_operator`] ? 'p-invalid' : ''}
                      />
                      {errors?.[`condition_${fi}_operator`] && (
                        <small className="p-error">{errors[`condition_${fi}_operator`]}</small>
                      )}
                    </div>

                    <div className="field-group field-value">
                      <label>Value</label>
                      <InputText
                        value={condition.value}
                        onChange={(e) => handleConditionChange(gi, ci, 'value', e.target.value)}
                        placeholder="Value to match"
                        className={errors?.[`condition_${fi}_value`] ? 'p-invalid' : ''}
                      />
                      {errors?.[`condition_${fi}_value`] && (
                        <small className="p-error">{errors[`condition_${fi}_value`]}</small>
                      )}
                    </div>

                    <div className="field-group field-checkbox">
                      <div className="p-field-checkbox">
                        <Checkbox
                          inputId={`case-${fi}`}
                          checked={condition.caseSensitive}
                          onChange={(e) => handleConditionChange(gi, ci, 'caseSensitive', e.checked)}
                        />
                        <label htmlFor={`case-${fi}`}>Case Sensitive</label>
                      </div>
                    </div>

                    <div className="field-actions">
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-text p-button-sm"
                        onClick={() => handleRemoveCondition(gi, ci)}
                        disabled={groups.length === 1 && group.conditions.length === 1}
                        tooltip="Remove condition"
                        type="button"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </React.Fragment>
      ))}

      {errors?.conditions && (
        <small className="p-error">{errors.conditions}</small>
      )}
    </div>
  );
};

ConditionBuilder.propTypes = {
  conditions: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    operator: PropTypes.string,
    value: PropTypes.string,
    caseSensitive: PropTypes.bool,
    logicalOperator: PropTypes.string
  })).isRequired,
  onConditionsChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  excelHeaders: PropTypes.array,
  isEditMode: PropTypes.bool,
  defaultLogicalOperator: PropTypes.string
};

export default ConditionBuilder;

